/**
 * BudgetCalculator.js – modal to set annual living expenses from ASFA Comfortable Standard categories.
 * Used in Client Profile Step 7 (Income/Expenses). Props: onClose, onSave, isPartnered.
 */
import React, { useState, useMemo } from 'react';
import { Calculator, X } from 'lucide-react';
import { CurrencyInput } from './ui/index.tsx';
import { formatCurrency } from '../utils/index.ts';
import { ASFA_BUDGET } from '../constants/index.ts';

interface BudgetCalculatorProps {
    onClose: () => void;
    onSave: (total: number) => void;
    isPartnered: boolean;
}

const BudgetCalculator = ({ onClose, onSave, isPartnered }: BudgetCalculatorProps) => {
    const budgetType = isPartnered ? 'couple' : 'single';
    const initialBudget = ASFA_BUDGET[budgetType].categories;
    const [budget, setBudget] = useState<Record<string, number>>(initialBudget);

    const handleUpdate = (category: string, value: number | undefined) => {
        setBudget(prev => ({ ...prev, [category]: value ?? 0 }));
    };

    const total = useMemo(() => Object.values(budget).reduce((sum, val) => sum + val, 0), [budget]);

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl max-h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-2xl font-bold text-gray-800 flex items-center gap-2"><Calculator /> Budget Calculator</h3>
                    <button onClick={onClose} className="p-2 hover:bg-gray-200 rounded-full transition-all duration-300 hover:scale-110"><X /></button>
                </div>
                <p className="text-sm text-gray-600 mb-4">These figures are based on the ASFA Comfortable Standard for the March 2025 quarter. Adjust them to match your personal spending.</p>
                <div className="flex-1 overflow-y-auto pr-2 space-y-4">
                    {Object.entries(budget).map(([category, amount]) => (
                        <div key={category} className="grid grid-cols-2 items-center gap-4">
                            <label className="font-medium text-gray-700">{category}</label>
                            <CurrencyInput name={category} value={amount} onChange={(v) => handleUpdate(category, v)} />
                        </div>
                    ))}
                </div>
                <div className="mt-6 pt-4 border-t flex justify-between items-center">
                    <p className="text-xl font-bold">Total: {formatCurrency(total)}</p>
                    <button onClick={() => onSave(total)} className="px-6 py-2 bg-gradient-to-r from-blue-500 to-teal-400 text-white font-semibold rounded-lg shadow-md hover:shadow-lg">
                        Save Budget
                    </button>
                </div>
            </div>
        </div>
    );
};

export default BudgetCalculator;
